import { RefObject, useCallback, useEffect, useRef } from "react";
import { useIntersectionObserver } from "@uidotdev/usehooks";

/**
 * Where the top of the latest message should land in the viewport when
 * scrolling to it:
 * - "top"    → top of the latest message at the top of the viewport
 * - "middle" → top of the latest message at 50% of the viewport height
 * - "bottom" → classic pin to the very bottom of the list
 *
 * For "top" and "middle", short last messages still end pinned to the
 * bottom: the requested position lies past the container's max scroll,
 * and scrollBy clamps there.
 */
export type ScrollToLatestPosition = "top" | "middle" | "bottom";

export const DEFAULT_SCROLL_TO_LATEST_POSITION: ScrollToLatestPosition =
  "middle";

const POSITION_VIEWPORT_RATIO: Record<
  Exclude<ScrollToLatestPosition, "bottom">,
  number
> = {
  top: 0,
  middle: 0.5,
};

/**
 * Scroll delta (px, positive = down) that places the top of the latest
 * message row at the configured viewport fraction.
 *
 * Works on rect deltas instead of absolute scrollTop because the message
 * lists are flex-col-reverse containers, where scrollTop uses a negative
 * coordinate space (0 = bottom); relative deltas are coordinate-space
 * agnostic.
 */
export function computeScrollToLatestDelta(
  rowTop: number,
  scrollerTop: number,
  scrollerClientHeight: number,
  position: Exclude<ScrollToLatestPosition, "bottom">,
): number {
  return (
    rowTop -
    (scrollerTop + scrollerClientHeight * POSITION_VIEWPORT_RATIO[position])
  );
}

/**
 * Describes the newest message so the hook can auto-scroll to it when the
 * current user sends one. Omit to opt a list out of send auto-scroll.
 */
export type AutoScrollOnSend = {
  /**
   * Stable identity of the newest message (index 0). Prefer the client id so
   * the optimistic → server id reconciliation of the same message does not
   * count as a new message. `null` when the conversation has no messages.
   */
  newestMessageId: string | null;
  /** Whether the newest message was sent by the current user. */
  newestIsOutgoing: boolean;
  /**
   * Identifies the open conversation (chat, or chat+thread). Changing it
   * resets the baseline so ids are never compared across conversations —
   * opening a chat is handled by its own scroll effect. Derive it from the
   * SAME source as `newestMessageId` (the newest message object) so the two
   * can't be transiently inconsistent while an async live query catches up to
   * a chat switch. `null` when the conversation has no messages loaded yet.
   */
  conversationKey: string | null;
};

/**
 * Shared "scroll to latest message" for the chat and thread message lists.
 * Owns the whole apparatus: the IntersectionObserver on the newest message
 * row, the derived button visibility, the scroll action, and the auto-scroll
 * when the current user sends a message — so the two lists can't drift on any
 * of it.
 *
 * Contract with the consumer:
 * - attach `latestRowRef` to the newest message row (index 0 in the
 *   flex-col-reverse flow); rows carry `data-index`, which the scroll
 *   action uses as a fallback before the observer has fired
 * - render the scroll button when `showScrollToBottom` is true
 * - pass `autoScrollOnSend` to auto-scroll to the sender's own new message
 *
 * @param scrollerRef Outer relative container — the observer root
 * @param messagesListRef Inner flex-col-reverse container that scrolls
 * @param autoScrollOnSend Newest-message signal for send auto-scroll
 */
export function useScrollToLatestMessage(
  scrollerRef: RefObject<HTMLDivElement>,
  messagesListRef: RefObject<HTMLDivElement>,
  autoScrollOnSend?: AutoScrollOnSend,
) {
  // The button hides as soon as any part of the latest message is visible,
  // so after a scroll-to-latest the user is never left with a button that
  // needs a second press to clear.
  const [latestRowRef, latestRowEntry] =
    useIntersectionObserver<HTMLDivElement>({
      threshold: 0,
      root: scrollerRef.current,
      rootMargin: "0px",
    });

  // Derived, not stored. The observer library never resets its entry after
  // the observed node unmounts, so a stale entry must not show the button —
  // e.g. when the open chat's messages are emptied in place (bulk delete),
  // no new row mounts to refresh the entry. `isConnected` catches that on
  // the re-render the emptying itself triggers. No entry yet (empty chat /
  // row not observed) → hidden.
  const showScrollToBottom = latestRowEntry
    ? latestRowEntry.target.isConnected && !latestRowEntry.isIntersecting
    : false;

  const scrollToLatestMessage = useCallback(
    (options?: {
      position?: ScrollToLatestPosition;
      behavior?: ScrollBehavior;
    }) => {
      const scroller = messagesListRef.current;
      if (!scroller) {
        return;
      }

      const {
        position = DEFAULT_SCROLL_TO_LATEST_POSITION,
        // Instant jump by default — matches the pre-existing button feel;
        // pass behavior: "smooth" explicitly to animate.
        behavior = "auto",
      } = options ?? {};

      // Prefer the node the observer is already tracking so the scroll
      // target and the visibility logic can't diverge; fall back to the
      // data-index query for presses before the observer has fired.
      const observed = latestRowEntry?.target;
      const latestRow = observed?.isConnected
        ? observed
        : scroller.querySelector('[data-index="0"]');

      if (position === "bottom" || !latestRow) {
        // scrollTop max is 0 in flex-col-reverse; scrollHeight clamps to it.
        scroller.scrollTo({ top: scroller.scrollHeight, behavior });
        return;
      }

      const delta = computeScrollToLatestDelta(
        latestRow.getBoundingClientRect().top,
        scroller.getBoundingClientRect().top,
        scroller.clientHeight,
        position,
      );
      scroller.scrollBy({ top: delta, behavior });
    },
    [messagesListRef, latestRowEntry],
  );

  // Auto-scroll to the newest message when the current user sends one, even if
  // they were scrolled up reading history — so the sender immediately sees
  // their own message (csov/ow2-chat-fe#867). Inbound messages while scrolled
  // up are intentionally left alone (the scroll-to-latest button appears
  // instead). Keyed on the newest message's stable id so a status/id
  // reconciliation of the same message does not re-yank the viewport; the
  // baseline resets on conversation change so ids are never compared across
  // conversations. Pins to the bottom, which — unlike the observer-driven
  // "middle" path — is immune to the IntersectionObserver lagging a tick
  // behind the just-inserted row.
  const prevNewestIdRef = useRef<string | null>(null);
  const prevConversationKeyRef = useRef<string | null>(null);
  const newestMessageId = autoScrollOnSend?.newestMessageId ?? null;
  const newestIsOutgoing = autoScrollOnSend?.newestIsOutgoing ?? false;
  const conversationKey = autoScrollOnSend?.conversationKey ?? null;
  const enabled = autoScrollOnSend !== undefined;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (conversationKey !== prevConversationKeyRef.current) {
      // Conversation changed — establish a baseline without scrolling.
      prevConversationKeyRef.current = conversationKey;
      prevNewestIdRef.current = newestMessageId;
      return;
    }
    if (newestMessageId && newestMessageId !== prevNewestIdRef.current) {
      prevNewestIdRef.current = newestMessageId;
      if (newestIsOutgoing) {
        scrollToLatestMessage({ position: "bottom" });
      }
    }
  }, [
    enabled,
    conversationKey,
    newestMessageId,
    newestIsOutgoing,
    scrollToLatestMessage,
  ]);

  return { latestRowRef, showScrollToBottom, scrollToLatestMessage };
}
